import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { 
  RefreshCw, 
  PlayCircle, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle,
  Activity,
  Calendar,
  Database,
  ShieldOff
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { API_URL } from '../../config/api';
import { useToast } from '../../components/ui/use-toast';
import { useAuth } from '../../context/auth-context';

interface ETLRun {
  id: number;
  site_id: string;
  site_name: string;
  process_date: string;
  process_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  records_processed: number;
  error_message?: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
}

interface ETLStatus {
  lastRun?: ETLRun;
  nextScheduledRun: string;
  isRunning: boolean;
  recentRuns: ETLRun[];
  failedRuns: ETLRun[];
  stats: {
    totalRuns: number;
    successfulRuns: number;
    failedRuns: number;
    avgDuration: number;
    totalRecordsProcessed: number;
  };
}

interface Site {
  id?: string;
  _id?: string;
  name: string;
  url: string;
}

const ETLMonitoring: React.FC = () => {
  const [status, setStatus] = useState<ETLStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSite, setSelectedSite] = useState<string>('all');
  const [selectedChatbotSite, setSelectedChatbotSite] = useState<string | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Chatbot Analytics ETL state
  const [chatbotETLStatus, setChatbotETLStatus] = useState<any>(null);
  const [chatbotETLLoading, setChatbotETLLoading] = useState(false);
  
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Check if user is super admin - only after auth has loaded
  useEffect(() => {
    // Don't check permissions while auth is still loading
    if (authLoading) return;
    
    // If auth has loaded and user is not super_admin, redirect
    if (user && user.role !== 'super_admin') {
      navigate('/dashboard');
    }
  }, [user, navigate, authLoading]);

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/analytics/etl-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch ETL status');
      }

      const data = await response.json();
      setStatus(data);
    } catch (error) {
      console.error('Error fetching ETL status:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch ETL status',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchChatbotETLStatus = async () => {
    setChatbotETLLoading(true);
    try {
      const response = await fetch(`${API_URL}/analytics/chatbot-etl-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Chatbot ETL status');
      }

      const data = await response.json();
      setChatbotETLStatus(data);
    } catch (error) {
      console.error('Error fetching Chatbot ETL status:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch Chatbot ETL status',
        variant: 'destructive',
      });
    } finally {
      setChatbotETLLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API_URL}/sites`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sites');
      }

      const data = await response.json();
      const sitesArray = Array.isArray(data) ? data : data.sites || [];
      setSites(sitesArray);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchSites();
    fetchChatbotETLStatus();

    // Auto-refresh every 30 seconds
    const interval = autoRefresh ? setInterval(() => {
      fetchStatus();
      fetchChatbotETLStatus();
    }, 30000) : null;
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const triggerETL = async () => {
    setTriggering(true);
    try {
      const body: any = {};
      if (selectedSite !== 'all') {
        body.siteId = selectedSite;
      }
      if (selectedDate) {
        body.date = selectedDate;
      }

      const response = await fetch(`${API_URL}/analytics/etl-trigger`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error('Failed to trigger ETL');
      }

      toast({
        title: 'ETL Triggered',
        description: 'The ETL pipeline has been started',
      });

      // Refresh status after a short delay
      setTimeout(fetchStatus, 2000);
    } catch (error) {
      console.error('Error triggering ETL:', error);
      toast({
        title: 'Error',
        description: 'Failed to trigger ETL pipeline',
        variant: 'destructive',
      });
    } finally {
      setTriggering(false);
    }
  };

  const triggerChatbotETL = async () => {
    if (!selectedChatbotSite) {
      toast({
        title: 'Error',
        description: 'Please select a specific site for Chatbot ETL',
        variant: 'destructive',
      });
      return;
    }

    setChatbotETLLoading(true);
    try {
      const response = await fetch(`${API_URL}/analytics/chatbot-etl-trigger`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          siteId: selectedChatbotSite,
          days: 7
        })
      });

      if (!response.ok) {
        throw new Error('Failed to trigger Chatbot ETL');
      }

      toast({
        title: 'Chatbot ETL Triggered',
        description: 'The Chatbot Analytics ETL has been started',
      });

      // Refresh status after a short delay
      setTimeout(fetchChatbotETLStatus, 2000);
    } catch (error) {
      console.error('Error triggering Chatbot ETL:', error);
      toast({
        title: 'Error',
        description: 'Failed to trigger Chatbot ETL',
        variant: 'destructive',
      });
    } finally {
      setChatbotETLLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500 dark:text-red-400" />;
      case 'running':
        return <RefreshCw className="h-4 w-4 text-blue-500 dark:text-blue-400 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500 dark:text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      completed: { className: 'bg-green-100 text-green-800 dark:bg-green-950/30 dark:text-green-200' },
      failed: { className: 'bg-red-100 text-red-800 dark:bg-red-950/30 dark:text-red-200' },
      running: { className: 'bg-blue-100 text-blue-800 dark:bg-blue-950/30 dark:text-blue-200' },
      pending: { className: 'bg-gray-100 text-gray-800 dark:bg-gray-800/30 dark:text-gray-200' }
    };

    return (
      <Badge variant="outline" className={variants[status]?.className || ''}>
        {status}
      </Badge>
    );
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Show loading state while auth is being checked
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        <span className="ml-2">Loading authentication...</span>
      </div>
    );
  }

  // Show access denied for non-super admins
  if (!user || user.role !== 'super_admin') {
    return null; // Don't render anything while redirecting
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">ETL Pipeline Monitoring</h1>
          <p className="text-muted-foreground">
            Monitor and manage the analytics data processing pipeline
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? 'bg-green-50' : ''}
          >
            <Activity className="h-4 w-4 mr-2" />
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => {
            fetchStatus();
            fetchChatbotETLStatus();
          }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Pipeline Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              {status?.isRunning ? (
                <>
                  <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />
                  <span className="text-2xl font-bold text-blue-600">Running</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-2xl font-bold text-green-600">Idle</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Last Run</CardTitle>
          </CardHeader>
          <CardContent>
            {status?.lastRun ? (
              <div>
                <p className="text-2xl font-bold">
                  {formatDistanceToNow(new Date(status.lastRun.started_at), { addSuffix: true })}
                </p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(status.lastRun.started_at), 'MMM d, yyyy h:mm a')}
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground">No runs yet</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Next Scheduled</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-lg font-semibold">
                  {formatDistanceToNow(new Date(status?.nextScheduledRun || Date.now()), { addSuffix: true })}
                </p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(status?.nextScheduledRun || Date.now()), 'h:mm a')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
          </CardHeader>
          <CardContent>
            {status?.stats ? (
              <div>
                <p className="text-2xl font-bold">
                  {((status.stats.successfulRuns / status.stats.totalRuns) * 100).toFixed(1)}%
                </p>
                <p className="text-sm text-muted-foreground">
                  {status.stats.successfulRuns} of {status.stats.totalRuns} runs
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground">No data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Manual Trigger */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <PlayCircle className="h-5 w-5 mr-2" />
            Manual ETL Trigger
          </CardTitle>
          <CardDescription>
            Manually run the ETL pipeline for a specific site and date
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium">Site</label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {sites.map((site, index) => (
                    <SelectItem key={`general-${site._id || site.id}-${index}`} value={site._id || site.id || ''}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1">
              <label className="text-sm font-medium">Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded-md"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <Button 
              onClick={triggerETL} 
              disabled={triggering || status?.isRunning}
              className="min-w-[120px]"
            >
              {triggering ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Run ETL
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Chatbot Analytics ETL Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            Chatbot Analytics ETL
          </CardTitle>
          <CardDescription>
            Real-time processing of chatbot conversations and analytics
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chatbotETLLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : chatbotETLStatus ? (
            <div className="space-y-4">
              {/* Chatbot ETL Status Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center space-x-2">
                    {chatbotETLStatus.status === 'running' ? (
                      <>
                        <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />
                        <span className="font-semibold text-blue-600">Running</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="font-semibold text-green-600">Active</span>
                      </>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Service Status</p>
                </div>
                
                <div className="p-4 border rounded-lg">
                  <div className="text-lg font-bold">
                    {chatbotETLStatus.processedConversations || 0}
                  </div>
                  <p className="text-sm text-muted-foreground">Processed Conversations</p>
                </div>
                
                <div className="p-4 border rounded-lg">
                  <div className="text-lg font-bold">
                    {chatbotETLStatus.queueSize || 0}
                  </div>
                  <p className="text-sm text-muted-foreground">Queue Size</p>
                </div>
              </div>

              {/* Manual Trigger for Chatbot ETL */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Manual Trigger (Last 7 Days)</h4>
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <label className="text-sm font-medium">Site (Required)</label>
                    <Select value={selectedChatbotSite || ''} onValueChange={setSelectedChatbotSite}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select site" />
                      </SelectTrigger>
                      <SelectContent>
                        {sites.map((site, index) => {
                          const siteId = site._id || site.id || '';
                          return (
                            <SelectItem key={`chatbot-${siteId}-${index}`} value={siteId}>
                              {site.name}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button 
                    onClick={triggerChatbotETL} 
                    disabled={chatbotETLLoading || !selectedChatbotSite}
                    className="min-w-[140px]"
                  >
                    {chatbotETLLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <PlayCircle className="h-4 w-4 mr-2" />
                        Process Chatbot
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Next scheduled run: {chatbotETLStatus.nextScheduledRun}
                </p>
              </div>
            </div>
          ) : (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Chatbot ETL Service Unavailable</AlertTitle>
              <AlertDescription>
                The Chatbot Analytics ETL service is not running or not responding.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Failed Runs Alert */}
      {status?.failedRuns && status.failedRuns.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Failed ETL Runs</AlertTitle>
          <AlertDescription>
            There are {status.failedRuns.length} failed ETL runs that may need attention.
          </AlertDescription>
        </Alert>
      )}

      {/* Recent Runs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Database className="h-5 w-5 mr-2" />
            Recent ETL Runs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Site</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Records</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {status?.recentRuns?.length ? (
                status.recentRuns.map((run) => (
                  <TableRow key={run.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(run.status)}
                        {getStatusBadge(run.status)}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {run.site_name || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      {format(new Date(run.process_date), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      {format(new Date(run.started_at), 'h:mm a')}
                    </TableCell>
                    <TableCell>
                      {formatDuration(run.duration_seconds)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {run.records_processed.toLocaleString()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {run.error_message && (
                        <span className="text-sm text-red-600" title={run.error_message}>
                          {run.error_message.substring(0, 50)}...
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    No ETL runs found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ETLMonitoring;